// Opens the camera (PERSONAL / front-facing first) with robust fallbacks, can
// flip between front and back, can cycle through every enumerated camera (used
// to auto-hunt for a face), and exposes native zoom where supported. The <video>
// displays the feed NATIVELY (GPU-composited) so startup is cheap on mobile.

interface ZoomCapability {
  min: number;
  max: number;
  step: number;
}

export type Facing = "user" | "environment";

/** Constraint fallbacks for a facing mode; first that succeeds wins. */
function facingConstraints(facing: Facing): MediaStreamConstraints[] {
  return [
    { audio: false, video: { facingMode: { ideal: facing }, width: { ideal: 1280 }, height: { ideal: 720 } } },
    { audio: false, video: { facingMode: { ideal: facing } } },
    { audio: false, video: true },
  ];
}

export class Camera {
  private stream: MediaStream | null = null;
  private track: MediaStreamTrack | null = null;
  private videoEl: HTMLVideoElement | null = null;
  private devices: MediaDeviceInfo[] = [];
  private deviceIdx = -1;
  /** Personal (front) camera first, per product requirement. */
  public facing: Facing = "user";

  async start(video: HTMLVideoElement, facing: Facing = this.facing): Promise<void> {
    this.videoEl = video;
    this.facing = facing;
    await this.open(facingConstraints(facing));
    void this.refreshDevices();
  }

  /** (Re)open the stream from the first constraint that succeeds. */
  private async open(constraintsList: MediaStreamConstraints[]): Promise<void> {
    this.stop();
    let lastErr: unknown;
    for (const c of constraintsList) {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia(c);
        break;
      } catch (e) {
        lastErr = e;
      }
    }
    if (!this.stream) throw lastErr ?? new Error("camera unavailable");

    this.track = this.stream.getVideoTracks()[0] ?? null;
    if (this.videoEl) {
      this.videoEl.srcObject = this.stream;
      // Autoplay backup; a rejected/interrupted play must NEVER block start.
      void this.videoEl.play().catch(() => {});
    }
    if (this.zoomCapability()) void this.setZoomLinear(0);
  }

  /** Enumerate video input devices (labels appear only after a grant). */
  async refreshDevices(): Promise<void> {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      this.devices = all.filter((d) => d.kind === "videoinput");
    } catch {
      // ignore; flip() still works via facingMode
    }
  }

  get cameraCount(): number {
    return this.devices.length;
  }

  /** Flip between front and back cameras. Returns the new facing. */
  async flip(): Promise<Facing> {
    this.facing = this.facing === "user" ? "environment" : "user";
    await this.open(facingConstraints(this.facing));
    return this.facing;
  }

  /**
   * Advance to the next enumerated camera (for devices with more than two).
   * Falls back to a front/back flip when device ids are not exposed.
   */
  async cycleDevice(): Promise<void> {
    if (this.devices.length < 2) {
      await this.flip();
      return;
    }
    this.deviceIdx = (this.deviceIdx + 1) % this.devices.length;
    const id = this.devices[this.deviceIdx]!.deviceId;
    await this.open([
      { audio: false, video: { deviceId: { exact: id } } },
      { audio: false, video: true },
    ]);
  }

  /** Native zoom range, or null if unsupported. */
  zoomCapability(): ZoomCapability | null {
    if (!this.track) return null;
    const caps = this.track.getCapabilities?.() as MediaTrackCapabilities & {
      zoom?: { min: number; max: number; step?: number };
    };
    const z = caps?.zoom;
    if (!z || typeof z.min !== "number" || typeof z.max !== "number" || z.max <= z.min) return null;
    return { min: z.min, max: z.max, step: z.step ?? (z.max - z.min) / 100 };
  }

  /** Set zoom from a normalized 0..1 value (0 = widest). */
  async setZoomLinear(t: number): Promise<void> {
    const cap = this.zoomCapability();
    if (!cap || !this.track) return;
    const value = cap.min + (cap.max - cap.min) * Math.max(0, Math.min(1, t));
    try {
      await this.track.applyConstraints({ advanced: [{ zoom: value }] } as unknown as MediaTrackConstraints);
    } catch (err) {
      console.warn("[camera] zoom not applied:", err);
    }
  }

  get videoTrack(): MediaStreamTrack | null {
    return this.track;
  }

  stop(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.track = null;
  }
}
